import type { Block } from '@ttab/elephant-api/newsdoc'
import type { BulkGetItem } from '@ttab/elephant-api/repository'
import type { Repository } from '@/shared/Repository'

export const FACTBOX_SUBSET_NAME = 'factbox'
export const FACTBOX_SUBSET_DSL = `${FACTBOX_SUBSET_NAME}=.content(type='core/factbox')`

/**
 * Bulk-gets the `core/factbox` blocks of the given articles, keyed by article uuid.
 *
 * The index flattens repeated block subfields into one array per leaf, so two
 * subfields of the same factbox can't be reassociated from a hit alone. Reading the
 * blocks themselves is the only way to correlate them. Blocks stay in `content`
 * order, so index `i` is the i-th factbox of the article - the same ordinal that
 * `:embedded:N` ids and `plugins/Factboxes/consume.ts` use.
 */
export const fetchFactboxBlocksByArticle = async ({ articleIds, repository, accessToken }: {
  articleIds: string[]
  repository: Repository
  accessToken: string
}): Promise<Map<string, Block[]>> => {
  const blocksByArticle = new Map<string, Block[]>()

  if (!articleIds.length) {
    return blocksByArticle
  }

  // Repository.getDocuments batches past the BulkGet cap itself.
  const response = await repository.getDocuments({
    documents: articleIds.map((uuid) => ({ uuid })),
    accessToken,
    subset: [FACTBOX_SUBSET_DSL]
  })

  const allItems: BulkGetItem[] = response?.items ?? []

  for (const item of allItems) {
    if (!item.uuid) {
      continue
    }

    const blocks: Block[] = (item.subset ?? [])
      .map((entry) => entry.values?.[FACTBOX_SUBSET_NAME]?.block)
      .filter((block): block is Block => block !== undefined)

    blocksByArticle.set(item.uuid, blocks)
  }

  return blocksByArticle
}
